import ReactECharts from 'echarts-for-react'
import { calculateItemMetrics, formatDateDisplay } from '@/lib/metrics'
import type { MetricsDataItem, DurationMetricsItem } from '@/entities/dailyMetric'

interface ChartsBlockProps {
  metricsData: MetricsDataItem[]
  durationMetrics: DurationMetricsItem[]
}

export function ChartsBlock({ metricsData, durationMetrics }: ChartsBlockProps) {
  if (!metricsData || metricsData.length === 0) {
    return null
  }

  // Sort metrics by date (ascending - oldest to newest, left to right)
  const sortedMetrics = [...metricsData].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  // Create a lookup map for duration metrics by date
  const durationMetricsByDate = new Map(durationMetrics.map((item) => [item.date, item]))

  // Prepare data for charts using pre-calculated metrics
  const dates = sortedMetrics.map((item) => formatDateDisplay(item.date))
  const fullDates = sortedMetrics.map((item) => formatDateDisplay(item.date, 'full'))

  // Use calculateItemMetrics to get all pre-calculated metrics
  const chartMetrics = sortedMetrics.map((item) => calculateItemMetrics(item, { raw: true }))

  const totalCalls = chartMetrics.map((item) => (item['Total calls'] as number) || 0)
  const connectivityRates = chartMetrics.map((item) => (item['% Connected to customer'] as number) || 0)
  const successRates = chartMetrics.map((item) => (item['% Successful calls'] as number) || 0)
  const totalCosts = chartMetrics.map((item) => (item['Cost ($)'] as number) || 0)
  const costPerSuccess = chartMetrics.map((item) => (item['Cost per success'] as number) || 0)

  // For calls over 60s, map based on the sorted dates to ensure alignment
  const callsOver60s = sortedMetrics.map((item) => {
    const durationData = durationMetricsByDate.get(item.date)
    const over60sPercent = durationData?.metrics?.['% 60s+'] || 0
    return over60sPercent
  })

  // Calculate proper averages for reference lines
  const sumTotalCalls = totalCalls.reduce((a, b) => a + b, 0)
  const avgTotalCalls = sumTotalCalls / totalCalls.length

  // For connectivity: weighted average based on total connected / total calls
  const totalConnectedCalls = chartMetrics.reduce(
    (acc, item) => acc + ((item['Connected to customer'] as number) || 0),
    0
  )
  const avgConnectivity = sumTotalCalls > 0 ? (totalConnectedCalls / sumTotalCalls) * 100 : 0

  // For success rate: weighted average based on total successful / total connected calls
  const totalSuccessfulCalls = chartMetrics.reduce(
    (acc, item) => acc + ((item['Successful calls'] as number) || 0),
    0
  )
  const avgSuccessRate = totalConnectedCalls > 0 ? (totalSuccessfulCalls / totalConnectedCalls) * 100 : 0

  // For total cost: simple sum divided by number of days
  const sumTotalCost = totalCosts.reduce((a, b) => a + b, 0)
  const avgTotalCost = sumTotalCost / totalCosts.length

  // For cost per success: total cost / total successful calls
  const avgCostPerSuccess = totalSuccessfulCalls > 0 ? sumTotalCost / totalSuccessfulCalls : 0

  // For calls over 60s: weighted average
  const totalCallsOver60sCount = durationMetrics.reduce((acc, item) => {
    const total = item.metrics?.['Total'] || 0
    const percent = item.metrics?.['% 60s+'] || 0
    return acc + (total * percent) / 100
  }, 0)
  const totalDurationCalls = durationMetrics.reduce((acc, item) => acc + (item.metrics?.['Total'] || 0), 0)
  const avgCallsOver60s = totalDurationCalls > 0 ? (totalCallsOver60sCount / totalDurationCalls) * 100 : 0

  // Common chart options with improved grid and tooltip
  const commonOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        lineStyle: {
          color: '#666',
          width: 1,
          type: 'dashed',
        },
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#ddd',
      borderWidth: 1,
      textStyle: {
        color: '#333',
        fontSize: 13,
      },
      padding: 12,
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: 45,
        fontSize: 11,
      },
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#666',
          width: 1,
          type: 'dashed',
        },
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '8%',
      top: '18%',
      containLabel: true,
    },
  }

  // Compute a nice Y-axis range that keeps the data visually meaningful.
  // For percentage charts the axis hugs the data with ~5pp padding instead of
  // always spanning 0-100 (which flattens lines clustered around e.g. 70%).
  const computeYAxisRange = (data: number[], isPercent: boolean) => {
    if (!isPercent) return {}
    const min = Math.min(...data)
    const max = Math.max(...data)
    const range = max - min
    const padding = Math.max(range * 1.2, 4)
    return {
      min: Math.max(0, Math.floor(min - padding)),
      max: Math.min(100, Math.ceil(max + padding)),
    }
  }

  // Chart configurations
  const createChartOption = (
    name: string,
    data: number[],
    color: string,
    avgValue: number,
    formatter: (params: { dataIndex: number; value: number }[]) => string
  ) => {
    const isPercent = name.includes('%') || name.includes('Rate')
    return {
    ...commonOptions,
    tooltip: {
      ...commonOptions.tooltip,
      formatter,
    },
    yAxis: {
      type: 'value',
      ...computeYAxisRange(data, isPercent),
    },
    series: [
      {
        name,
        type: 'line',
        data,
        smooth: 0.4,
        smoothMonotone: 'x',
        lineStyle: { color, width: 2.5 },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: color.replace(')', ', 0.25)').replace('rgb', 'rgba') },
              { offset: 1, color: color.replace(')', ', 0.05)').replace('rgb', 'rgba') },
            ],
          },
        },
        itemStyle: { color },
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color, type: 'dashed', width: 2, opacity: 0.6 },
          label: {
            formatter: name.includes('$')
              ? `Avg: $${avgValue.toFixed(2)}`
              : name.includes('%')
                ? `Avg: ${avgValue.toFixed(2)}%`
                : `Avg: ${avgValue.toFixed(0)}`,
            position: 'insideEndTop',
            fontSize: 11,
            color,
          },
          data: [{ yAxis: avgValue }],
        },
      },
    ],
  }
  }

  const colors = {
    blue: '#6B8DB2',
    red: '#B07A7A',
    green: '#7BA69E',
    magenta: '#A68B9A',
    cyan: '#7BABB8',
    orange: '#B89A7B',
  }

  const totalCallsOption = createChartOption(
    'Total Calls',
    totalCalls,
    colors.blue,
    avgTotalCalls,
    (params) =>
      `<b>${fullDates[params[0].dataIndex]}</b><br/>${params[0].value.toLocaleString()} calls<br/><span style="color: #999;">Avg: ${avgTotalCalls.toFixed(0)} calls</span>`
  )

  const connectivityOption = createChartOption(
    'Connectivity Rate',
    connectivityRates,
    colors.red,
    avgConnectivity,
    (params) =>
      `<b>${fullDates[params[0].dataIndex]}</b><br/>${params[0].value.toFixed(2)}%<br/><span style="color: #999;">Avg: ${avgConnectivity.toFixed(2)}%</span>`
  )

  const successRateOption = createChartOption(
    'Success Rate',
    successRates,
    colors.green,
    avgSuccessRate,
    (params) =>
      `<b>${fullDates[params[0].dataIndex]}</b><br/>${params[0].value.toFixed(2)}%<br/><span style="color: #999;">Avg: ${avgSuccessRate.toFixed(2)}%</span>`
  )

  const totalCostOption = createChartOption(
    'Total Cost',
    totalCosts,
    colors.magenta,
    avgTotalCost,
    (params) =>
      `<b>${fullDates[params[0].dataIndex]}</b><br/>$${params[0].value.toFixed(2)}<br/><span style="color: #999;">Avg: $${avgTotalCost.toFixed(2)}</span>`
  )

  const callsOver60sOption = createChartOption(
    'Calls over 60s',
    callsOver60s,
    colors.cyan,
    avgCallsOver60s,
    (params) =>
      `<b>${fullDates[params[0].dataIndex]}</b><br/>${params[0].value.toFixed(2)}%<br/><span style="color: #999;">Avg: ${avgCallsOver60s.toFixed(2)}%</span>`
  )

  const costPerSuccessOption = createChartOption(
    'Cost per Success',
    costPerSuccess,
    colors.orange,
    avgCostPerSuccess,
    (params) =>
      `<b>${fullDates[params[0].dataIndex]}</b><br/>$${params[0].value.toFixed(2)}<br/><span style="color: #999;">Avg: $${avgCostPerSuccess.toFixed(2)}</span>`
  )

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-center text-body font-medium mb-4">Total Calls</h3>
        <ReactECharts option={totalCallsOption} style={{ height: '200px' }} />
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-center text-body font-medium mb-4">Connectivity (%)</h3>
        <ReactECharts option={connectivityOption} style={{ height: '200px' }} />
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-center text-body font-medium mb-4">Success Rate (%)</h3>
        <ReactECharts option={successRateOption} style={{ height: '200px' }} />
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-center text-body font-medium mb-4">Total Cost ($)</h3>
        <ReactECharts option={totalCostOption} style={{ height: '200px' }} />
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-center text-body font-medium mb-4">Calls over 60s (%)</h3>
        <ReactECharts option={callsOver60sOption} style={{ height: '200px' }} />
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6 border">
        <h3 className="text-center text-body font-medium mb-4">Cost per Success ($)</h3>
        <ReactECharts option={costPerSuccessOption} style={{ height: '200px' }} />
      </div>
    </div>
  )
}